import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Search,
  Filter,
  Eye,
  Calendar,
  User,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  X
} from 'lucide-react'

interface Booking {
  id: string
  booking_reference: string
  guest_name: string
  guest_email: string
  item_type: 'hotel' | 'activity' | 'package'
  item_name: string
  check_in_date?: string
  check_out_date?: string
  activity_date?: string
  guests: {
    adults: number
    children: number
    infants: number
  }
  total_amount: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed'
  payment_status: 'pending' | 'paid' | 'refunded'
  created_at: string
}

interface BookingManagementProps {
  tenantId: string
}

const BookingManagement: React.FC<BookingManagementProps> = ({ tenantId }) => {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  
  // Modal states
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  
  // Edit/Cancel form states
  const [editStatus, setEditStatus] = useState('')
  const [cancellationReason, setCancellationReason] = useState('')
  const [cancellationCharges, setCancellationCharges] = useState(0)

  useEffect(() => {
    fetchBookings()
  }, [tenantId])

  const fetchBookings = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/bookings?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        setBookings(data.bookings)
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateBookingStatus = async (bookingId: string, status: string) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/bookings/${bookingId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, tenant_id: tenantId })
      })
      
      if (response.ok) {
        setBookings(prev => prev.map(booking => 
          booking.id === bookingId ? { ...booking, status: status as any } : booking
        ))
      }
    } catch (error) {
      console.error('Failed to update booking status:', error)
    }
  }

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking)
    setShowViewModal(true)
  }

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking)
    setEditStatus(booking.status)
    setShowEditModal(true)
  }

  const handleCancelBooking = (booking: Booking) => {
    setSelectedBooking(booking)
    setCancellationReason('')
    setCancellationCharges(0)
    setShowCancelModal(true)
  }

  const submitStatusUpdate = async () => {
    if (selectedBooking && editStatus !== selectedBooking.status) {
      await updateBookingStatus(selectedBooking.id, editStatus)
      setShowEditModal(false)
      setSelectedBooking(null)
    }
  }

  const submitCancellation = async () => {
    if (!selectedBooking) return

    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/bookings/${selectedBooking.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: tenantId,
          cancellation_reason: cancellationReason,
          cancellation_charges: cancellationCharges
        })
      })
      
      if (response.ok) {
        setBookings(prev => prev.map(booking => 
          booking.id === selectedBooking.id ? { ...booking, status: 'cancelled' } : booking
        ))
        setShowCancelModal(false)
        setSelectedBooking(null)
        alert('Booking cancelled successfully. Inventory has been replenished.')
      }
    } catch (error) {
      console.error('Failed to cancel booking:', error)
      alert('Failed to cancel booking. Please try again.')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      case 'completed': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'refunded': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.booking_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.item_name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus
    const matchesType = filterType === 'all' || booking.item_type === filterType
    return matchesSearch && matchesStatus && matchesType
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Booking Management</h1>
        <p className="text-gray-600">Monitor and manage customer bookings</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Total Bookings</p>
              <p className="text-2xl font-semibold text-gray-900">{bookings.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Confirmed</p>
              <p className="text-2xl font-semibold text-gray-900">
                {bookings.filter(b => b.status === 'confirmed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">
                {bookings.filter(b => b.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500">Total Revenue</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${bookings.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + b.total_amount, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by guest name, booking reference, or item..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="hotel">Hotels</option>
              <option value="activity">Activities</option>
              <option value="package">Packages</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBookings.map((booking) => (
                <tr key={booking.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 text-left">
                        {booking.booking_reference}
                      </div>
                      <div className="text-sm text-gray-500 text-left">
                        {booking.item_name}
                      </div>
                      <div className="text-xs text-purple-600 capitalize text-left">
                        {booking.item_type}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 text-left">
                        {booking.guest_name}
                      </div>
                      <div className="text-sm text-gray-500 text-left">
                        {booking.guest_email}
                      </div>
                      <div className="text-xs text-gray-500 text-left">
                        {booking.guests.adults} adults, {booking.guests.children} children
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    <div className="text-left">
                      {booking.check_in_date && booking.check_out_date ? (
                        <div className="text-left">
                          <div className="text-left">Check-in: {new Date(booking.check_in_date).toLocaleDateString()}</div>
                          <div className="text-left">Check-out: {new Date(booking.check_out_date).toLocaleDateString()}</div>
                        </div>
                      ) : booking.activity_date ? (
                        <div className="text-left">Date: {new Date(booking.activity_date).toLocaleDateString()}</div>
                      ) : (
                        <div className="text-left">TBD</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 text-left">
                        ${booking.total_amount.toLocaleString()}
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(booking.payment_status)}`}>
                        {booking.payment_status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <div className="text-left">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                    <div className="flex items-center gap-2 justify-start">
                      <button 
                        onClick={() => handleViewBooking(booking)}
                        className="text-blue-600 hover:text-blue-700"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleEditBooking(booking)}
                        className="text-purple-600 hover:text-purple-700"
                        title="Edit Booking"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {booking.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancelBooking(booking)}
                          className="text-red-600 hover:text-red-700"
                          title="Cancel Booking"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredBookings.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all' || filterType !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Bookings will appear here once customers start making reservations.'}
          </p>
        </div>
      )}

      {/* View Booking Modal */}
      {showViewModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Booking Details</h2>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-gray-700">Booking Reference</h3>
                <p className="text-gray-600">{selectedBooking.booking_reference}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Status</h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Guest Name</h3>
                <p className="text-gray-600">{selectedBooking.guest_name}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Guest Email</h3>
                <p className="text-gray-600">{selectedBooking.guest_email}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Item Type</h3>
                <p className="text-gray-600 capitalize">{selectedBooking.item_type}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Item Name</h3>
                <p className="text-gray-600">{selectedBooking.item_name}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Guests</h3>
                <p className="text-gray-600">{selectedBooking.guests.adults} adults, {selectedBooking.guests.children} children</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Total Amount</h3>
                <p className="text-gray-600">${selectedBooking.total_amount.toLocaleString()}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Payment Status</h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(selectedBooking.payment_status)}`}>
                  {selectedBooking.payment_status}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Created At</h3>
                <p className="text-gray-600">{new Date(selectedBooking.created_at).toLocaleString()}</p>
              </div>
              {selectedBooking.check_in_date && (
                <div>
                  <h3 className="font-semibold text-gray-700">Check-in Date</h3>
                  <p className="text-gray-600">{new Date(selectedBooking.check_in_date).toLocaleDateString()}</p>
                </div>
              )}
              {selectedBooking.check_out_date && (
                <div>
                  <h3 className="font-semibold text-gray-700">Check-out Date</h3>
                  <p className="text-gray-600">{new Date(selectedBooking.check_out_date).toLocaleDateString()}</p>
                </div>
              )}
              {selectedBooking.activity_date && (
                <div>
                  <h3 className="font-semibold text-gray-700">Activity Date</h3>
                  <p className="text-gray-600">{new Date(selectedBooking.activity_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {showEditModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Edit Booking</h2>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Booking: {selectedBooking.booking_reference}</h3>
                <p className="text-sm text-gray-600">{selectedBooking.item_name}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={submitStatusUpdate}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md"
                >
                  Update Status
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Cancel Booking</h2>
              <button 
                onClick={() => setShowCancelModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <h3 className="font-semibold text-red-800">Booking: {selectedBooking.booking_reference}</h3>
                <p className="text-sm text-red-700">{selectedBooking.item_name}</p>
                <p className="text-sm text-red-700">Amount: ${selectedBooking.total_amount.toLocaleString()}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Reason</label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Enter reason for cancellation..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500 h-20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Charges ($)</label>
                <input
                  type="number"
                  value={cancellationCharges}
                  onChange={(e) => setCancellationCharges(Number(e.target.value))}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-red-500 focus:border-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Refund Amount: ${(selectedBooking.total_amount - cancellationCharges).toLocaleString()}
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ For hotel bookings, inventory will be automatically replenished upon cancellation.
                </p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={submitCancellation}
                  disabled={!cancellationReason.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-4 py-2 rounded-md"
                >
                  Cancel Booking
                </button>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                >
                  Keep Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BookingManagement